<template>
  <div class="knowledge-detail-page" v-if="article">
    <section class="detail-hero" aria-labelledby="article-title">
      <img :src="article.image" :alt="article.title + '文章配图'" />
      <div class="detail-hero-mask"></div>
      <div class="detail-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <router-link to="/knowledge">工程检测知识库</router-link>
          <span>/</span>
          <span>{{ article.title }}</span>
        </nav>
        <p class="eyebrow">{{ article.category }}</p>
        <h1 id="article-title">{{ article.title }}</h1>
        <p>{{ article.summary }}</p>
        <div class="article-meta">
          <span>{{ article.category }}</span>
          <span>{{ article.date }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section-inner article-layout">
        <article class="article-main">
          <div class="summary-box">
            <h2>文章要点</h2>
            <ul>
              <li v-for="point in article.points" :key="point">{{ point }}</li>
            </ul>
          </div>

          <section
            class="article-section"
            v-for="section in article.content"
            :key="section.heading"
          >
            <h2>{{ section.heading }}</h2>
            <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </section>

          <section class="article-section">
            <h2>相关服务</h2>
            <div class="service-links">
              <router-link v-for="service in services" :key="service.slug" :to="service.path">
                {{ service.shortTitle }}
              </router-link>
            </div>
          </section>
        </article>

        <aside class="side-panel">
          <h2>更多知识内容</h2>
          <router-link
            v-for="item in relatedArticles"
            :key="item.id"
            :to="'/knowledge/' + item.id"
          >
            <span>{{ item.category }}</span>
            <strong>{{ item.title }}</strong>
          </router-link>
          <router-link class="side-cta" :to="{ path: '/', hash: '#lead-form' }">获取项目检测方案</router-link>
        </aside>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">苏ICP备2024065706号</a>
      <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=32050602013590" target="_blank" rel="noopener">苏公网安备32050602013590号</a>
    </div>
  </div>
</template>

<script>
import { newsArticles, services } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "NewsDetails",
  data() {
    return {
      services: services
    };
  },
  computed: {
    article() {
      const id = Number(this.$route.params.id);
      return newsArticles.find(function(item) {
        return item.id === id;
      });
    },
    relatedArticles() {
      if (!this.article) {
        return [];
      }
      return newsArticles
        .filter(item => item.id !== this.article.id)
        .slice(0, 4);
    }
  },
  mounted() {
    this.applyPageSeo();
  },
  watch: {
    "$route.params.id"() {
      this.applyPageSeo();
    }
  },
  methods: {
    applyPageSeo() {
      if (!this.article) {
        this.$router.replace("/knowledge");
        return;
      }

      setSeo({
        title: this.article.title + "｜江苏道鼎升工程检测知识库",
        description: this.article.summary,
        path: "/knowledge/" + this.article.id,
        image: this.article.image,
        type: "article"
      });

      setStructuredData([
        organizationSchema(),
        localBusinessSchema(),
        {
          "@type": "Article",
          headline: this.article.title,
          description: this.article.summary,
          image: absoluteUrl(this.article.image),
          mainEntityOfPage: absoluteUrl("/knowledge/" + this.article.id),
          publisher: {
            "@id": absoluteUrl("/#organization")
          },
          articleSection: this.article.category
        },
        breadcrumbSchema([
          { name: "首页", path: "/" },
          { name: "工程检测知识库", path: "/knowledge" },
          { name: this.article.title, path: "/knowledge/" + this.article.id }
        ])
      ]);
    }
  }
};
</script>

<style lang="scss" scoped>
.knowledge-detail-page {
  background: #f5f8f7;
  color: #1f2d3d;
}

.detail-hero {
  min-height: 450px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.detail-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(12, 38, 51, 0.94), rgba(15, 76, 92, 0.72));
}

.detail-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.detail-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #d76b30;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.detail-hero h1 {
  max-width: 900px;
  font-size: 42px;
  line-height: 1.25;
  margin: 0 0 18px;
}

.detail-hero p {
  max-width: 860px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
}

.article-meta span {
  padding: 8px 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

.section {
  padding: 72px 0;
}

.article-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;
}

.article-main,
.side-panel {
  border: 1px solid #dce6e4;
  border-radius: 8px;
  background: #fff;
}

.article-main {
  padding: 36px;
}

.summary-box {
  padding: 26px;
  border-radius: 8px;
  background: #e8f3f1;
  margin-bottom: 42px;
}

h2 {
  color: #0f4c5c;
  font-size: 29px;
  line-height: 1.35;
  margin: 0 0 16px;
}

.summary-box ul {
  margin: 0;
  padding-left: 18px;
  color: #3d4b56;
  line-height: 1.85;
}

.article-section + .article-section {
  margin-top: 44px;
  padding-top: 38px;
  border-top: 1px solid #edf2f1;
}

.article-section p {
  color: #3d4b56;
  line-height: 1.95;
  margin: 0 0 18px;
  font-size: 16px;
}

.service-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.service-links a {
  padding: 10px 13px;
  border-radius: 4px;
  background: #e8f3f1;
  color: #0f4c5c;
  text-decoration: none;
  font-weight: 700;
}

.side-panel {
  position: sticky;
  top: 84px;
  padding: 24px;
}

.side-panel h2 {
  font-size: 24px;
}

.side-panel a {
  display: block;
  text-decoration: none;
  color: inherit;
}

.side-panel a:not(.side-cta) {
  border-top: 1px solid #edf2f1;
  padding: 16px 0;
}

.side-panel span {
  display: block;
  color: #18736e;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 7px;
}

.side-panel strong {
  display: block;
  color: #17313b;
  line-height: 1.55;
}

.side-cta {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 4px;
  background: #18736e;
  color: #fff !important;
  margin-top: 18px;
}

@media (max-width: 980px) {
  .article-layout {
    grid-template-columns: 1fr;
  }

  .detail-hero h1 {
    font-size: 34px;
  }

  .side-panel {
    position: static;
  }
}

@media (max-width: 640px) {
  .detail-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .detail-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .detail-hero h1 {
    font-size: 28px;
  }

  .section {
    padding: 52px 0;
  }

  .article-main {
    padding: 24px 18px;
  }
}
</style>
